import React, { forwardRef } from 'react';
import { InputAdornment, StandardTextFieldProps, TextField } from '@mui/material';
import { FieldError } from 'react-hook-form';
import styles from './index.module.scss';
import classNames from 'classnames';
import { ReactComponent as SearchIcon } from '../../../assets/icons/patients/leadingIcon.svg';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

export interface TextFieldProps extends Omit<StandardTextFieldProps, 'variant' | 'helperText'> {
  isReadonly?: boolean;
  format?: string;
  helperText?: Partial<FieldError> | string;
  wrapperClassName?: string;
  inputClassName?: string;
  disabledClassName?: string;
  variant?: 'outlined' | 'standard' | 'filled';
  hideErrorIcon?: boolean;
  wrapText?: boolean;
  serachInputClassName?: string;
}

export default forwardRef((props: TextFieldProps, ref) => {
  const {
    error,
    isReadonly,
    placeholder,
    wrapperClassName,
    label,
    type,
    inputClassName,
    disabledClassName,
    hideErrorIcon,
    wrapText,
    serachInputClassName,
    ...textFieldProps
  } = props;

  const isSearchable = type === 'search';

  return (
    <TextField
      error={error}
      disabled={isReadonly}
      placeholder={placeholder}
      inputRef={ref}
      label={label}
      type={type}
      className={classNames(styles.root, wrapperClassName)}
      FormHelperTextProps={{
        classes: {
          root: classNames(styles.helperTextRoot, 'text-xs'),
        },
      }}
      {...(textFieldProps as StandardTextFieldProps)}
      InputLabelProps={{
        ...(placeholder ||
        type === 'date' ||
        textFieldProps?.variant !== 'outlined' ||
        textFieldProps?.disabled
          ? { shrink: true }
          : {}),
        classes: {
          root: styles.labelRoot,
          shrink: classNames(styles.shrink, textFieldProps?.InputLabelProps?.classes?.shrink),
          disabled: styles.disabled,
          outlined: styles.focusedLabel,
          error: styles.error,
        },
      }}
      InputProps={{
        ...(textFieldProps?.variant !== 'outlined' && {
          disableUnderline: true,
        }),
        classes: {
          root: classNames(
            styles.inputRoot,
            inputClassName,
            'rounded-4 border border-[var(--neutrals-n-400)]',
            {
              [classNames(
                styles.serachInput,
                'border-[var(--neutrals-n-300)]',
                serachInputClassName,
              )]: isSearchable && !label,
              'rounded-[44px]': isSearchable && !label && !serachInputClassName,
            },
          ),
          input: wrapText ? styles.inputWrapped : undefined,
          disabled: classNames(styles.disabled, disabledClassName),
          error: styles.inputError,
        },
        startAdornment: isSearchable ? (
          <InputAdornment position="start">
            <SearchIcon className={styles.searchIcon} />
          </InputAdornment>
        ) : null,
        endAdornment:
          error && !hideErrorIcon ? (
            <InputAdornment position="end">
              <ErrorOutlineOutlinedIcon className={styles.errorIcon} />
            </InputAdornment>
          ) : null,
        ...textFieldProps.InputProps,
      }}
    />
  );
});
